import {
  useAddDatabaseItemsApi,
  useCreateTableViewApi,
  useCreateTableViewChatApi,
  useCreateTableViewColumnApi,
  useDeleteTableViewApi,
  useDeleteTableViewColumnApi,
  useDeleteTableViewRowsApi,
  useExecuteTableViewAnalysisApi,
  useExportTableViewApi,
  useFilePresignedUrlsApi,
  useGenerateTableViewColumnsApi,
  useGetFileApi,
  useGetTableViewApi,
  useGetTableViewChatApi,
  useGetTableViewPresignedUrlsApi,
  useGetTemplatesApi,
  useInitializeFromProductCodeApi,
  useLinkFilesApi,
  useListDatabaseQueriesApi,
  useListDatabaseSourcesApi,
  useListLinkedFilesApi,
  useListTableViewsApi,
  useListWorkspaceFilesApi,
  useRegenerateCellApi,
  useRegisterTableViewFileOnlyApi,
  useRegisterTableViewFilesApi,
  useReorderTableViewColumnsApi,
  useSetDatabaseActiveApi,
  useTestDatabaseQueryApi,
  useUnlinkFilesApi,
  useUpdateTableViewApi,
  useUpdateTableViewColumnApi,
  useUseTemplateApi,
} from '@/api/tableViews';
import { useAuthenticatedClient, useWorkspaceBaseUrl } from '@/api/useApi';
import {
  HandleSubmitMessageType,
  HandleSubmitOptionsType,
} from '@/utils/aiUtils';
import { useChat } from '@ai-sdk/react';
import { useAuth } from '@clerk/clerk-react';
import type {
  DtUIMessage,
  TableViewFilters,
  TableViewRegisterFileResponse,
} from '@ditto/shared';
import { DefaultChatTransport } from 'ai';
import { useEffect, useMemo, useState } from 'react';

export const useTableViewsService = () => {
  const { data, ...rest } = useListTableViewsApi();

  const tableViews = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { tableViews, ...rest };
};

export const useTableViewService = (id: string, filters?: TableViewFilters) => {
  const { data, ...rest } = useGetTableViewApi(id, filters, {
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return false;
      if (data.isGenerating) return 2000;
      return false;
    },
  });

  const isGenerating = useMemo(() => {
    if (!data) return false;
    return data.isGenerating;
  }, [data]);

  return { tableView: data, isGenerating, ...rest };
};

// Helper function to upload file to S3
const uploadToS3 = async (file: File, presignedUrl: string): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
};

// Helper function to upload files to S3 in batches
const uploadFilesToS3InBatches = async (
  files: File[],
  uploadUrls: Array<{ key: string; url: string; fileName: string }>
): Promise<{
  uploadResults: Array<{ success: boolean; file: File; error?: string }>;
  completedUploads: Array<{
    s3Key: string;
    fileName: string;
    size: number;
    mimeType: string;
    file: File;
  }>;
}> => {
  const batchSize = 10;
  const uploadResults: Array<{ success: boolean; file: File; error?: string }> =
    [];
  const completedUploads: Array<{
    s3Key: string;
    fileName: string;
    size: number;
    mimeType: string;
    file: File;
  }> = [];

  for (let i = 0; i < files.length; i += batchSize) {
    const batch = files.slice(i, i + batchSize);
    const urlBatch = uploadUrls.slice(i, i + batchSize);

    const batchPromises = batch.map(async (file, index) => {
      const uploadUrl = urlBatch[index];

      try {
        // Upload to S3
        await uploadToS3(file, uploadUrl.url);

        completedUploads.push({
          s3Key: uploadUrl.key,
          fileName: file.name,
          size: file.size,
          mimeType: file.type,
          file,
        });

        return { success: true, file };
      } catch (error) {
        return {
          success: false,
          file,
          error: error instanceof Error ? error.message : 'Upload failed',
        };
      }
    });

    const batchResults = await Promise.allSettled(batchPromises);
    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        uploadResults.push(result.value);
      } else {
        uploadResults.push({
          success: false,
          file: batch[0],
          error: result.reason,
        });
      }
    });
  }

  return { uploadResults, completedUploads };
};

export const useTableViewUploadService = () => {
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const getPresignedUrlsMutation = useGetTableViewPresignedUrlsApi();
  const registerFilesMutation = useRegisterTableViewFilesApi();

  const uploadFiles = async (tableViewId: string, files: File[]) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User not authenticated');
    }

    setUploadingFiles(files);

    try {
      // Step 1: Get presigned URLs for all files
      const fileInfos = files.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
      }));

      const uploadUrls = await getPresignedUrlsMutation.mutateAsync({
        tableViewId,
        files: fileInfos,
      });

      // Step 2: Upload files to S3 in parallel (batched)
      const { uploadResults, completedUploads } =
        await uploadFilesToS3InBatches(files, uploadUrls);

      // Step 3: Register all successfully uploaded files in database
      let registeredFiles: TableViewRegisterFileResponse[] = [];
      if (completedUploads.length > 0) {
        registeredFiles = await registerFilesMutation.mutateAsync({
          tableViewId,
          files: completedUploads.map(upload => ({
            s3Key: upload.s3Key,
            fileName: upload.fileName,
            size: upload.size,
            mimeType: upload.mimeType,
          })),
        });
      }
      return { uploadResults, registeredFiles };
    } finally {
      setUploadingFiles([]);
    }
  };

  const isUploading = uploadingFiles.length > 0;

  return {
    uploadFiles,
    uploadingFiles,
    isUploading,
    error: getPresignedUrlsMutation.error || registerFilesMutation.error,
    isLoaded,
    isSignedIn,
  };
};
export const useCreateTableViewService = () => {
  const {
    mutate: createTableView,
    isPending: isCreating,
    ...rest
  } = useCreateTableViewApi();

  return { createTableView, isCreating, ...rest };
};

export const useUpdateTableViewService = () => {
  const {
    mutate: updateTableView,
    isPending: isUpdateTableViewPending,
    ...rest
  } = useUpdateTableViewApi();

  return { updateTableView, isUpdateTableViewPending, ...rest };
};

export const useDeleteTableViewService = () => {
  const {
    mutate: deleteTableView,
    isPending: isDeletingTableView,
    ...rest
  } = useDeleteTableViewApi();

  return { deleteTableView, isDeletingTableView, ...rest };
};

export const useGenerateTableViewColumnsService = () => {
  const {
    mutate: generateTableViewColumns,
    isPending: isGeneratingColumns,
    ...rest
  } = useGenerateTableViewColumnsApi();

  return { generateTableViewColumns, isGeneratingColumns, ...rest };
};

export const useCreateTableViewColumnService = () => {
  const { mutate: createTableViewColumn, ...rest } =
    useCreateTableViewColumnApi();

  return { createTableViewColumn, ...rest };
};

export const useUpdateTableViewColumnService = () => {
  const { mutate: updateTableViewColumn, ...rest } =
    useUpdateTableViewColumnApi();

  return { updateTableViewColumn, ...rest };
};

export const useDeleteTableViewColumnService = () => {
  const { mutate: deleteTableViewColumn, ...rest } =
    useDeleteTableViewColumnApi();

  return { deleteTableViewColumn, ...rest };
};

export const useReorderTableViewColumnsService = () => {
  const { mutate: reorderColumns, ...rest } = useReorderTableViewColumnsApi();

  return { reorderColumns, ...rest };
};

export const useExecuteTableViewAnalysisService = () => {
  const {
    mutate: executeAnalysis,
    isPending: isExecuting,
    ...rest
  } = useExecuteTableViewAnalysisApi();

  return { executeAnalysis, isExecuting, ...rest };
};

export const useGetFileService = (fileId: string | undefined) => {
  const { data, ...rest } = useGetFileApi(fileId);

  return {
    file: data,
    fileUrl: data?.signedUrl,
    ...rest,
  };
};

export const useExportTableViewService = () => {
  const {
    mutateAsync: exportTableView,
    isPending: isExporting,
    ...rest
  } = useExportTableViewApi();

  const downloadExport = async (tableViewId: string) => {
    const result = await exportTableView({ tableViewId });
    // Open the signed URL in a new tab to trigger download
    window.open(result.signedUrl, '_blank');
    return result;
  };

  return { downloadExport, isExporting, ...rest };
};

export const useDownloadPapersService = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const baseUrl = useWorkspaceBaseUrl();
  const { getToken } = useAuth();

  const downloadPapersAsZip = async (
    rows: {
      id: string;
      label: string | null;
      source: string | null;
      cells: { columnId: string; textValue: string | null }[];
    }[],
    columns: { id: string; name: string }[]
  ) => {
    // Find the PDF URL and Title columns
    const pdfUrlColumn = columns.find(col => col.name === 'PDF URL');
    const titleColumn = columns.find(col => col.name === 'Title');

    // Extract papers with PDF URLs from the rows
    const papers: { pdfUrl: string; title: string }[] = [];

    for (const row of rows) {
      // Only process pubmed and openalex sources
      if (row.source !== 'pubmed' && row.source !== 'openalex') continue;

      // Get PDF URL from the cell
      let pdfUrl: string | null = null;
      if (pdfUrlColumn) {
        const pdfUrlCell = row.cells.find(
          cell => cell.columnId === pdfUrlColumn.id
        );
        pdfUrl = pdfUrlCell?.textValue || null;
      }

      // Get title from cell or fall back to row label
      let title = row.label || 'Untitled';
      if (titleColumn) {
        const titleCell = row.cells.find(
          cell => cell.columnId === titleColumn.id
        );
        if (titleCell?.textValue) {
          title = titleCell.textValue;
        }
      }

      if (pdfUrl) {
        papers.push({ pdfUrl, title });
      }
    }

    const totalPaperRows = rows.filter(
      r => r.source === 'pubmed' || r.source === 'openalex'
    ).length;

    if (papers.length === 0) {
      return { downloadedCount: 0, skippedCount: totalPaperRows };
    }

    setIsDownloading(true);

    try {
      const token = await getToken();
      const response = await fetch(`${baseUrl}/table-views/download-papers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ papers }),
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Trigger browser download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `papers-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { downloadedCount: papers.length, skippedCount: 0 };
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadPapersAsZip,
    isDownloading,
  };
};

export const useInitializeFromProductCodeService = () => {
  const {
    mutate: initializeFromProductCode,
    isPending: isInitializing,
    ...rest
  } = useInitializeFromProductCodeApi();

  return { initializeFromProductCode, isInitializing, ...rest };
};

export const useRegenerateCellService = () => {
  const { mutate: regenerateCell, ...rest } = useRegenerateCellApi();

  return { regenerateCell, ...rest };
};

export const useDeleteTableViewRowsService = () => {
  const { mutate: deleteTableViewRows, ...rest } = useDeleteTableViewRowsApi();

  return { deleteTableViewRows, ...rest };
};

export const useGetTemplatesService = () => {
  const { data, ...rest } = useGetTemplatesApi();

  return { templates: data?.templates ?? [], ...rest };
};

export const useUseTemplateService = () => {
  const {
    mutate: useTemplate,
    isPending: isUsingTemplate,
    ...rest
  } = useUseTemplateApi();

  return { useTemplate, isUsingTemplate, ...rest };
};

export const useCreateTableViewChatService = () => {
  const { mutate: createTableViewChat, ...rest } = useCreateTableViewChatApi();

  return { createTableViewChat, ...rest };
};

export const useGetTableViewChatService = (tableViewId: string) => {
  const { data, ...rest } = useGetTableViewChatApi(tableViewId);
  return { tableViewChat: data?.chat ?? null, ...rest };
};

export const useTableViewChat = (
  tableViewId: string,
  initialMessages?: DtUIMessage[]
) => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const workspaceBaseUrl = useWorkspaceBaseUrl();

  const {
    messages,
    setMessages,
    sendMessage: sendMessageWithoutAuth,
    ...rest
  } = useChat<DtUIMessage>({
    experimental_throttle: 100,
    transport: new DefaultChatTransport({
      prepareSendMessagesRequest({ messages, body }) {
        return {
          api: `${workspaceBaseUrl}/table-views/${tableViewId}/chat/stream`,
          body: {
            ...body,
            tableViewId,
            message: messages[messages.length - 1],
          },
        };
      },
    }),
  });

  // Initialize messages from existing chat data
  useEffect(() => {
    if (
      initialMessages &&
      initialMessages.length > 0 &&
      messages.length === 0
    ) {
      setMessages(initialMessages);
    }
  }, [initialMessages, messages.length, setMessages]);

  const sendMessage = async (
    message: HandleSubmitMessageType,
    opts?: HandleSubmitOptionsType
  ) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User is not authenticated');
    }

    const token = await getToken();

    sendMessageWithoutAuth(message, {
      ...opts,
      body: {
        ...opts?.body,
        tableViewId,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        ...opts?.headers,
      },
    });
  };

  return {
    messages,
    setMessages,
    sendMessage,
    ...rest,
  };
};

// Database sources services
export const useDatabaseSourcesService = () => {
  const { data, ...rest } = useListDatabaseSourcesApi();

  return {
    sources: data ?? [],
    ...rest,
  };
};

export const useTestDatabaseQueryService = () => {
  const {
    mutateAsync: testQuery,
    isPending: isTesting,
    ...rest
  } = useTestDatabaseQueryApi();

  return { testQuery, isTesting, ...rest };
};

export const useAddDatabaseItemsService = () => {
  const {
    mutateAsync: addDatabaseItems,
    isPending: isAdding,
    ...rest
  } = useAddDatabaseItemsApi();

  return { addDatabaseItems, isAdding, ...rest };
};

// Saved database queries services
export const useDatabaseQueriesService = (tableViewId: string | undefined) => {
  const { data, ...rest } = useListDatabaseQueriesApi(tableViewId);

  return {
    savedQueries: data ?? [],
    ...rest,
  };
};

export const useSetDatabaseActiveService = () => {
  const { mutateAsync, isPending, ...rest } = useSetDatabaseActiveApi();

  return {
    setDatabaseActive: mutateAsync,
    isUpdating: isPending,
    ...rest,
  };
};

// Workspace files services
export const useWorkspaceFilesService = () => {
  const { data, ...rest } = useListWorkspaceFilesApi();

  return {
    files: data ?? [],
    ...rest,
  };
};

export const useLinkedFilesService = (tableViewId: string | undefined) => {
  const { data, ...rest } = useListLinkedFilesApi(tableViewId);

  return {
    linkedFiles: data ?? [],
    ...rest,
  };
};

export const useRegisterTableViewFileOnlyService = () => {
  const {
    mutateAsync: registerFile,
    isPending: isRegistering,
    ...rest
  } = useRegisterTableViewFileOnlyApi();

  return { registerFile, isRegistering, ...rest };
};

export const useLinkFilesService = () => {
  const {
    mutateAsync: linkFiles,
    isPending: isLinking,
    ...rest
  } = useLinkFilesApi();

  return { linkFiles, isLinking, ...rest };
};

export const useUnlinkFilesService = () => {
  const {
    mutateAsync: unlinkFiles,
    isPending: isUnlinking,
    ...rest
  } = useUnlinkFilesApi();

  return { unlinkFiles, isUnlinking, ...rest };
};

// Workspace file upload service (for uploading files to workspace without linking to a table)
export const useWorkspaceFileUploadService = () => {
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const getPresignedUrlsMutation = useFilePresignedUrlsApi();
  const registerFileMutation = useRegisterTableViewFileOnlyApi();

  const uploadFiles = async (files: File[]) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User not authenticated');
    }

    setUploadingFiles(files);

    try {
      // Step 1: Get presigned URLs for all files
      const fileInfos = files.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
      }));

      const uploadUrls = await getPresignedUrlsMutation.mutateAsync(fileInfos);

      // Step 2: Upload files to S3 in parallel (batched)
      const { completedUploads } = await uploadFilesToS3InBatches(
        files,
        uploadUrls
      );

      // Step 3: Register all successfully uploaded files
      const registeredFiles = await Promise.all(
        completedUploads.map(upload =>
          registerFileMutation.mutateAsync({
            s3Key: upload.s3Key,
            fileName: upload.fileName,
            size: upload.size,
            mimeType: upload.mimeType,
          })
        )
      );

      return { registeredFiles };
    } finally {
      setUploadingFiles([]);
    }
  };

  const isUploading = uploadingFiles.length > 0;

  return {
    uploadFiles,
    uploadingFiles,
    isUploading,
    error: getPresignedUrlsMutation.error || registerFileMutation.error,
    isLoaded,
    isSignedIn,
  };
};
